import { Component, Vue } from 'vue-property-decorator';
import ParticipantService from '@/services/participant.service';
import TokenService from '@/services/token.service';
import AuthenticationError from '@/exceptions/authentication.error';
import InputNumber from '@/components/inputs/InputNumber/InputNumber';
import InputDate from '@/components/inputs/InputDate/InputDate';
import moment from 'moment-timezone';
import { IdentificationDTO } from '@/models/IdentificationDTO';
import ModalTemplate from '@/components/ModalTemplate/ModalTemplate';
import UserService from '@/services/user.service';
import { RawLocation } from 'vue-router';
import { ActionTypes, GetterTypes } from '@/store-vuex/types';
import { isFirstAptAllowed, isRadiologueChoiceActivatedFromSU, isRdvLongUsedFromSU } from '@/config/app.config';
import AppointmentService from '@/services/appointment.service';
import { ParticipantDTO } from '@/models/ParticipantDTO';
import { AppointmentDTO } from '@/models/AppointmentDTO';
import { AppointmentDetailsDTO } from '@/models/AppointmentDetailsDTO';
import { Sex } from '@/models/constants/enums';
import InformationBlock from '@/components/InformationBlock/InformationBlock';
import { McsisConstants } from '@/models/constants/McsisConstants';
// tslint:disable-next-line: no-var-requires
const VTooltip = require('v-tooltip');
Vue.use(VTooltip);

@Component({
  components: {
    InputNumber,
    InputDate,
    ModalTemplate,
    InformationBlock,
  },
})
export default class ParticipantIdentification extends Vue {
  public errorMessage: string = '';
  private participantId?: number = 0;
  private dateOfBirth?: string = '';
  private wrongUrl: boolean = false;
  private showModal: boolean = false;
  private openedDossierId?: number = 0;
  private showErrInfoModal?: boolean = false;

  //#region ----------- Getter, Watcher & Hooks -------------

  public get errMsg(): string {
    return this.$store.getters[GetterTypes.popUpErrMessage];
  }

  public get isInfoBlockEmpty(): boolean {
    const translation = this.$t('comp.loginPage.informations').toString();
    return (translation === '' || translation === 'comp.loginPage.informations') ? true : false;
  }

  public get participant(): ParticipantDTO {
    return this.$store.getters[GetterTypes.participant];
  }

  public get allowFirstAppointment(): boolean {
      return this.$store.state.allowFirstAppointment;
  }

  public get isParticipantFirstAppointment(): boolean {
      return this.$store.state.isFirstAppointment;
  }

  /**
   * Initializes the component data
   */
  protected async mounted() {
    this.participantId = undefined;
    this.dateOfBirth = undefined;
  }

  //#endregion
  //#region ---------------- Service Handlers -------------

  /**
   * @brief essaie de se loguer avec les paramètres données
   * @param partId
   * @param dateOfBirth
   * @returns le nom du su auquel il est connecté ou undefined si cela échoue
   */
  protected async getLoginResponse(partId: number, dateOfBirth: string): Promise<string | undefined> {
    return await ParticipantService.login( partId, dateOfBirth, this.$route.params.screening)
      .then((data: IdentificationDTO) => {
        if (TokenService.tokenExists()) {
          this.$emit('identified', true);
          this.$store.dispatch(ActionTypes.setIsIdentified, true);

          return data.suName;
        }
      })
      .catch((error: AuthenticationError) => {
        this.errorMessage = error.message;
        this.showErrInfoModal = this.$store.getters[GetterTypes.showErrInfoModal];

        return undefined;
      });
  }

  /**
   * @brief redirige vers le bon su et réssaie de se connecter
   * @param suName
   */
  protected logoutFromFrontend(suName: string): void {
    UserService.logoutFromFrontend();
    this.$emit('identified', false);
    this.$store.dispatch(ActionTypes.setIsIdentified, false);
    this.$router.push({
      name: 'home',
      params: {
        screening: suName,
      },
    }).then(() => {
      this.login();
    });
  }

  /**
   * @brief récupère le token,les données du participant, son rdv et redirige vers la page suivante
   * @param partId
   * @param suName
   */
  protected async finishLogin(partId: number, suName: string): Promise<void> {
    await TokenService.generateXsrfToken()
    .then(async (data: any) => {
      TokenService.setXsrfTokenInHeader();

      await ParticipantService.fetchParticipant(partId)
        .then((part: ParticipantDTO) => {
          this.$store.dispatch(ActionTypes.setParticipant, part);
        });

      await this.getPartAppointment(partId);

      // Load parameters
      this.$store.dispatch(
        ActionTypes.setUseTRMGenderChoice,
        isRadiologueChoiceActivatedFromSU(this.$route.params.screening));
      this.$store.dispatch(
        ActionTypes.setUseLongAppointment,
        isRdvLongUsedFromSU(this.$route.params.screening));
      this.$store.dispatch(
        ActionTypes.setAllowFirstAppointment,
        isFirstAptAllowed(this.$route.params.screening));

      // Redirection
      if (this.$store.getters[GetterTypes.hasRdv]) {
        // Participant already has an appointment -> redirect to current appointment
          const nextLocation: RawLocation = {
            name: 'recap',
            params: {
              screening: suName,
              partId: String(partId),
            },
          };
          this.$router.push(nextLocation);
      } else {
        // Participant has no appointment in the current cycle
        // Redirect to appointment selection except if the current canton is tg
        // in which case we redirect to appointment-participant
        let nextLocationName = McsisConstants.AppointmentDetailsPage;

        if (this.showAppointmentParticipantStep) {
          nextLocationName = McsisConstants.AppointmentParticipantPage;
        }

        const nextLocation: RawLocation = {
          name: nextLocationName,
          params: {
            screening: suName,
            partId: String(partId),
          },
        };

        this.$router.push(nextLocation);
      }
    });
  }

  /**
   * @brief Logs the participant in.
   */
  protected async login(): Promise<void> {
    // set maVisitedStepNumber to 0 in order to avoid stepping unchecked step
    this.$emit('openingDossier');

    const partId = Number(this.participantId!);

    const dateOfBirth = moment(this.dateOfBirth!).format(McsisConstants.DefaultDateFormat);
    this.errorMessage = McsisConstants.EmptyString;

    const loginResponse = await this.getLoginResponse(partId, dateOfBirth);

    if (loginResponse !== undefined && loginResponse !== this.$route.params.screening) {
      this.logoutFromFrontend(loginResponse);

    } else if (loginResponse !== undefined && this.$store.getters[GetterTypes.isIdentified]) {
      await this.finishLogin(partId, loginResponse);

    }
  }

  /**
   * @brief get the participant data
   * @param partId
   */
  protected async getPartAppointment(partId: number): Promise<void> {
    await AppointmentService.GetAppointmentByPartId(partId)
      .then((res: AppointmentDTO) => {
        if (res.appId === undefined) {
          this.$store.dispatch(ActionTypes.setAppointment, {
              partId: this.participant.partId,
              appFirstname: this.participant.firstName,
              appLastname: this.participant.lastName,
              appBirthdate: this.participant.birthDate,
              appointmentDetails: {
                appdId: undefined,
                appId: undefined,
                trmGenderId: Sex.Undefined,
                isLongAppointment: false,
                longAppointmentReasonsId: [],
              } as AppointmentDetailsDTO,
            } as AppointmentDTO);
        } else {
          res.appDate = moment(res.appDate).format();
          res.appEndDate = moment(res.appEndDate).format();
          this.$store.dispatch(ActionTypes.setAppointment, res);
        }
      });

    if (this.$store.getters[GetterTypes.appointment].appId === undefined) {
      await ParticipantService.isFirstApt(partId)
      .then((res: boolean) => {
        this.$store.dispatch(ActionTypes.setIsFirstAppointment, res);
      });
    }
  }

  //#endregion
  //#region ---------------- Events Handlers -------------

  protected async onLoginClick() {
    await this.login();
  }

  protected hideModal() {
    this.showModal = false;
  }

  protected hideErrInfoModal() {
    this.$store.dispatch(ActionTypes.setShowErrInfoModal, false);
    this.$store.dispatch(ActionTypes.setPopUpErrMessage, '');
    this.showErrInfoModal = this.$store.getters[GetterTypes.showErrInfoModal];
  }

  protected hideWrongUrl() {
    this.wrongUrl = false;
  }

  /**
   * Gets true if AppointmentParticipant step must be displayed
   *
   * @returns {boolean} true or false
   */
  public get showAppointmentParticipantStep(): boolean {
      return this.$store.getters[GetterTypes.showAppointmentParticipantStep];
  }

  //#endregion
}
